export const LOGO_MAX_INPUT_BYTES = 2 * 1024 * 1024;
export const LOGO_MAX_DIM = 512;

const ALLOWED_LOGO_TYPES = ["image/png", "image/jpeg", "image/svg+xml"];

function canvasToBlob(canvas, type) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error("The browser could not process this image.")),
      type
    );
  });
}

export function hexToRgb01(hex) {
  const clean = String(hex || "").trim();
  const full =
    clean.length === 4
      ? `#${clean[1]}${clean[1]}${clean[2]}${clean[2]}${clean[3]}${clean[3]}`
      : clean;
  const match = /^#?([0-9a-f]{6})$/i.exec(full);

  if (!match) return { r: 0.5, g: 0.5, b: 0.5 };

  const value = parseInt(match[1], 16);

  return {
    r: ((value >> 16) & 0xff) / 255,
    g: ((value >> 8) & 0xff) / 255,
    b: (value & 0xff) / 255
  };
}

function parseSvgDimensions(text) {
  const num = "([0-9.]+)";
  const widthMatch = new RegExp(`width=["']${num}`).exec(text);
  const heightMatch = new RegExp(`height=["']${num}`).exec(text);
  const viewBoxMatch = /viewBox=["']([^"']+)["']/i.exec(text);

  if (viewBoxMatch) {
    const parts = viewBoxMatch[1].trim().split(/[\s,]+/).map(Number);

    if (parts.length === 4 && parts.every((n) => Number.isFinite(n))) {
      return {
        width: parts[2] - parts[0],
        height: parts[3] - parts[1],
        fromViewBox: true
      };
    }
  }

  return {
    width: widthMatch ? Number(widthMatch[1]) : NaN,
    height: heightMatch ? Number(heightMatch[1]) : NaN,
    fromViewBox: false
  };
}

export async function normalizeLogo(file) {
  if (!file) throw new Error("Choose a logo file first.");

  if (file.size > LOGO_MAX_INPUT_BYTES) {
    throw new Error(
      `That file is ${Math.round(file.size / 1024)} KB. The input limit is 2 MB.`
    );
  }

  const type = ALLOWED_LOGO_TYPES.includes(file.type) ? file.type : null;

  if (!type) {
    throw new Error("Logos must be PNG, JPEG, or SVG files.");
  }

  if (type === "image/svg+xml") {
    const raw = await file.text();
    const open = raw.toLowerCase().indexOf("<svg");

    if (open === -1) {
      throw new Error("That SVG file does not contain an <svg> element.");
    }

    // Strip script blocks; the logo is later rasterized/inlined for the PDF and
    // previewed via a data URL, so active content must never survive.
    const cleaned = raw
      .replace(/<script[\s\S]*?<\/script\s*>/gi, "")
      .replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, "")
      .replace(/\son[a-z]+\s*=\s*'[^']*'/gi, "");

    const dims = parseSvgDimensions(cleaned);

    // SVG scales without loss; cap the reported dimensions at 512 for
    // downstream layout, but never reject an SVG for its viewBox size.
    return {
      blob: new Blob([cleaned], { type: "image/svg+xml" }),
      type: "image/svg+xml",
      width: Math.min(
        LOGO_MAX_DIM,
        Math.max(1, Number.isFinite(dims.width) ? dims.width : 256)
      ),
      height: Math.min(
        LOGO_MAX_DIM,
        Math.max(1, Number.isFinite(dims.height) ? dims.height : 256)
      ),
      name: file.name || "logo.svg"
    };
  }

  let bitmap;

  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error("That image could not be read. Try another file.");
  }

  try {
    if (bitmap.width > LOGO_MAX_DIM || bitmap.height > LOGO_MAX_DIM) {
      throw new Error(
        `Logos must be at most ${LOGO_MAX_DIM} × ${LOGO_MAX_DIM} px. This image is ${bitmap.width} × ${bitmap.height} px.`
      );
    }

    const canvas = document.createElement("canvas");

    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas
      .getContext("2d", { alpha: true })
      .drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const blob = await canvasToBlob(canvas, "image/png");

    return {
      blob,
      type: "image/png",
      width: bitmap.width,
      height: bitmap.height,
      name: file.name || "logo.png"
    };
  } finally {
    bitmap.close();
  }
}

export async function svgToPngDataUrl(svgBlob, maxDim = LOGO_MAX_DIM) {
  const text =
    typeof svgBlob === "string"
      ? svgBlob
      : await svgBlob.text();
  const dataUrl = svgDataUrl(text);
  const image = new Image();

  const loaded = new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = () =>
      reject(new Error("The SVG logo could not be rasterized."));
  });

  image.src = dataUrl;
  await loaded;

  const scale = Math.min(
    1,
    maxDim / Math.max(image.naturalWidth || 1, image.naturalHeight || 1)
  );
  const width = Math.max(1, Math.round((image.naturalWidth || 256) * scale));
  const height = Math.max(1, Math.round((image.naturalHeight || 256) * scale));
  const canvas = document.createElement("canvas");

  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d", { alpha: true }).drawImage(image, 0, 0, width, height);

  return canvas.toDataURL("image/png");
}

export function svgDataUrl(svg) {
  const text = typeof svg === "string" ? svg : null;

  if (text === null) {
    throw new Error("svgDataUrl expects SVG text.");
  }

  return `data:image/svg+xml;base64,${window.btoa(unescape(encodeURIComponent(text)))}`;
}
